import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useContainer } from "@contexts/container-context";

/**
 * Hook to fetch details for a specific classroom
 *
 * @param {string} classroomId - The ID of the classroom
 * @param {Object} [options={}] - Additional options for react-query
 * @returns {Object} The result of useQuery containing data, status, etc.
 */
export const useClassroomDetails = (classroomId, options = {}) => {
  const { classroomService } = useContainer();
  return useQuery({
    queryKey: ["classroomDetails", classroomId],
    queryFn: () => classroomService.getClassroomDetails(classroomId),
    enabled: !!classroomId,
    ...options,
  });
};

/**
 * Hook to update details for a specific classroom using a mutation
 *
 * @param {string} classroomId - The ID of the classroom
 * @param {Object} [options={}] - Additional options for useMutation
 * @returns {Object} Result object from useMutation
 */
export const useUpdateClassroom = (classroomId, options = {}) => {
  const queryClient = useQueryClient();
  const { classroomService } = useContainer();

  return useMutation({
    mutationKey: ["updateClassroom", classroomId],
    /**
     * Function to update classroom details.
     * @param {Object} data - The data to update the classroom with.
     * @returns {Promise<any>}
     */
    mutationFn: (data) => classroomService.updateClassroom(classroomId, data),
    /**
     * On success, invalidate the classroom details query.
     *
     * @returns {void}
     */
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["classroomDetails", classroomId],
      });
    },
    ...options,
  });
};

/**
 * Hook to fetch instructors for a specific classroom
 *
 * @param {string} classroomId - The ID of the classroom
 * @param {Object} [options={}] - Additional options for react-query
 * @returns {Object} The result of useQuery containing data, status, etc.
 */
export const useClassroomInstructors = (classroomId, options = {}) => {
  const { classroomService } = useContainer();

  return useQuery({
    queryKey: ["classroomInstructors", classroomId],
    queryFn: () => classroomService.getClassroomInstructors(classroomId),
    enabled: !!classroomId,
    ...options,
  });
};

/**
 * Hook to fetch the trainees (learners) enrolled in a specific classroom.
 *
 * @param {string} classroomId - The ID of the classroom
 * @param {string} [status] - Record-status filter (e.g. "active"); omitted
 *   returns the service default
 * @param {Object} [options={}] - Additional options for react-query
 * @returns {Object} The result of useQuery containing data, status, etc.
 */
export const useClassroomTrainees = (classroomId, status, options = {}) => {
  const { classroomService } = useContainer();

  return useQuery({
    queryKey: ["classroomTrainees", classroomId, status],
    queryFn: () => classroomService.getClassroomTrainees(classroomId, status),
    enabled: !!classroomId,
    ...options,
  });
};

/**
 * Custom hook to remove instructors from a classroom using a mutation.
 *
 * @param {string} classroomId - The ID of the classroom.
 * @param {Object} [options={}] - Additional options for useMutation.
 * @returns {Object} Result object from useMutation.
 */
export const useRemoveClassroomInstructors = (classroomId, options = {}) => {
  const queryClient = useQueryClient();
  const { classroomService } = useContainer();

  return useMutation({
    mutationKey: ["removeClassroomInstructors", classroomId],
    /**
     * Function to remove instructors by user IDs.
     * @param {Array<string>} userIds - User IDs to remove as instructors.
     * @returns {Promise<any>}
     */
    mutationFn: (userIds) =>
      classroomService.removeInstructors(classroomId, userIds),
    /**
     * On success, invalidate the classroom instructors query.
     */
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["classroomInstructors", classroomId],
      });
    },
    ...options,
  });
};

/**
 * Custom hook to add instructors to a classroom using a mutation.
 *
 * @param {string} classroomId - The ID of the classroom.
 * @param {Object} [options={}] - Additional options for useMutation.
 * @returns {Object} Result object from useMutation.
 */
export const useAddClassroomInstructors = (classroomId, options = {}) => {
  const queryClient = useQueryClient();
  const { classroomService } = useContainer();

  return useMutation({
    mutationKey: ["addClassroomInstructors", classroomId],
    /**
     * Function to add instructors by user IDs.
     * @param {Array<string>} userIds - User IDs to add as instructors.
     * @returns {Promise<any>}
     */
    mutationFn: (userIds) =>
      classroomService.addInstructors(classroomId, userIds),
    /**
     * On success, invalidate the classroom instructors query.
     */
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["classroomInstructors", classroomId],
      });
    },
    ...options,
  });
};
